import { useReducer, useCallback } from 'react';
import { registerWaiting, WaitingResult } from '../api/waiting';

// --- 상태 타입 ---

export type PageName = 'welcome' | 'orderType' | 'menu' | 'phone' | 'partySize' | 'complete';

export interface OrderItemState {
  menuId: string;
  quantity: number;
}

export interface KioskState {
  currentPage: PageName;
  orderType: 'DINE_IN' | 'TAKEOUT';
  phone: string;
  partySize: number;
  orderItems: OrderItemState[];
  result: WaitingResult | null;
  isSubmitting: boolean;
  error: string | null;
}

// --- 액션 타입 ---

type KioskAction =
  | { type: 'GO_TO_WELCOME' }
  | { type: 'GO_TO_ORDER_TYPE' }
  | { type: 'SET_ORDER_TYPE'; payload: 'DINE_IN' | 'TAKEOUT' }
  | { type: 'GO_TO_MENU' }
  | { type: 'SET_ITEM_QUANTITY'; payload: { menuId: string; quantity: number } }
  | { type: 'GO_TO_PHONE' }
  | { type: 'SET_PHONE'; payload: string }
  | { type: 'APPEND_DIGIT'; payload: string }
  | { type: 'DELETE_DIGIT' }
  | { type: 'CLEAR_PHONE' }
  | { type: 'GO_TO_PARTY_SIZE' }
  | { type: 'SET_PARTY_SIZE'; payload: number }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; payload: WaitingResult }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'RESET' };

// --- 초기 상태 ---

const initialState: KioskState = {
  currentPage: 'welcome',
  orderType: 'DINE_IN',
  phone: '',
  partySize: 0,
  orderItems: [],
  result: null,
  isSubmitting: false,
  error: null,
};

// --- 리듀서 ---

function kioskReducer(state: KioskState, action: KioskAction): KioskState {
  switch (action.type) {
    case 'GO_TO_WELCOME':
      return { ...state, currentPage: 'welcome', error: null };

    case 'GO_TO_ORDER_TYPE':
      return { ...state, currentPage: 'orderType', error: null };

    case 'SET_ORDER_TYPE':
      return { ...state, orderType: action.payload, error: null };

    case 'GO_TO_MENU':
      return { ...state, currentPage: 'menu', error: null };

    case 'SET_ITEM_QUANTITY': {
      const { menuId, quantity } = action.payload;
      const others = state.orderItems.filter((i) => i.menuId !== menuId);
      const next = quantity > 0 ? [...others, { menuId, quantity }] : others;
      return { ...state, orderItems: next };
    }

    case 'GO_TO_PHONE':
      return { ...state, currentPage: 'phone', error: null };

    case 'SET_PHONE':
      return { ...state, phone: action.payload };

    case 'APPEND_DIGIT':
      if (state.phone.length >= 11) return state;
      return { ...state, phone: state.phone + action.payload };

    case 'DELETE_DIGIT':
      return { ...state, phone: state.phone.slice(0, -1) };

    case 'CLEAR_PHONE':
      return { ...state, phone: '' };

    case 'GO_TO_PARTY_SIZE':
      return { ...state, currentPage: 'partySize', error: null };

    case 'SET_PARTY_SIZE':
      return { ...state, partySize: action.payload };

    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: null };

    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        isSubmitting: false,
        result: action.payload,
        currentPage: 'complete',
      };

    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.payload };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

// --- 훅 ---

export interface KioskFlowActions {
  goToWelcome: () => void;
  goToOrderType: () => void;
  setOrderType: (type: 'DINE_IN' | 'TAKEOUT') => void;
  goToMenu: () => void;
  setItemQuantity: (menuId: string, quantity: number) => void;
  goToPhone: () => void;
  setPhone: (phone: string) => void;
  appendDigit: (digit: string) => void;
  deleteDigit: () => void;
  clearPhone: () => void;
  goToPartySize: () => void;
  setPartySize: (size: number) => void;
  submit: () => Promise<void>;
  reset: () => void;
}

export function useKioskFlow(
  storeId: string,
  language?: string,
  isDemo = false,
): [KioskState, KioskFlowActions] {
  const [state, dispatch] = useReducer(kioskReducer, initialState);

  const goToWelcome = useCallback(() => dispatch({ type: 'GO_TO_WELCOME' }), []);
  const goToOrderType = useCallback(() => dispatch({ type: 'GO_TO_ORDER_TYPE' }), []);
  const setOrderType = useCallback((type: 'DINE_IN' | 'TAKEOUT') => dispatch({ type: 'SET_ORDER_TYPE', payload: type }), []);
  const goToMenu = useCallback(() => dispatch({ type: 'GO_TO_MENU' }), []);
  const setItemQuantity = useCallback((menuId: string, quantity: number) => dispatch({ type: 'SET_ITEM_QUANTITY', payload: { menuId, quantity } }), []);
  const goToPhone = useCallback(() => dispatch({ type: 'GO_TO_PHONE' }), []);
  const setPhone = useCallback((phone: string) => dispatch({ type: 'SET_PHONE', payload: phone }), []);
  const appendDigit = useCallback((digit: string) => dispatch({ type: 'APPEND_DIGIT', payload: digit }), []);
  const deleteDigit = useCallback(() => dispatch({ type: 'DELETE_DIGIT' }), []);
  const clearPhone = useCallback(() => dispatch({ type: 'CLEAR_PHONE' }), []);
  const goToPartySize = useCallback(() => dispatch({ type: 'GO_TO_PARTY_SIZE' }), []);
  const setPartySize = useCallback((size: number) => dispatch({ type: 'SET_PARTY_SIZE', payload: size }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const submit = useCallback(async () => {
    dispatch({ type: 'SUBMIT_START' });

    // 데모/미리보기 모드: 실제 API 호출 대신 가짜 결과 반환
    if (isDemo) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const mockResult: WaitingResult = {
        waitingNumber: 3,
        ahead: 2,
        estimatedMinutes: 15,
      };
      dispatch({ type: 'SUBMIT_SUCCESS', payload: mockResult });
      return;
    }

    try {
      const orderItems = state.orderType === 'TAKEOUT' ? state.orderItems : undefined;
      const result = await registerWaiting(
        storeId,
        state.phone,
        state.partySize,
        language,
        state.orderType,
        orderItems,
      );
      dispatch({ type: 'SUBMIT_SUCCESS', payload: result });
    } catch (err) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
      dispatch({ type: 'SUBMIT_ERROR', payload: message });
    }
  }, [storeId, state.phone, state.partySize, state.orderType, state.orderItems, language, isDemo]);

  const actions: KioskFlowActions = {
    goToWelcome,
    goToOrderType,
    setOrderType,
    goToMenu,
    setItemQuantity,
    goToPhone,
    setPhone,
    appendDigit,
    deleteDigit,
    clearPhone,
    goToPartySize,
    setPartySize,
    submit,
    reset,
  };

  return [state, actions];
}
